import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { GoldDots, PageHead } from "@/components/Brand";
import { PriceCard } from "@/components/PriceCard";
import { errMsg, useApi } from "@/lib/api";
import { InterestPanel } from "@/components/InterestPanel";
import { CARD_NOTE, KEEP_NOTE, NZ_GST_NOTE, OUTSIDE_NZ, PRICING, REFUND_TERMS, RESTRICTED_NOTE } from "@/lib/content";
import { useFixCard } from "@/components/HeldPlan";
import { useMe } from "@/context/MeContext";


export default function Membership() {
  const [params] = useSearchParams();
  const api = useApi();
  const { me } = useMe();
  const [plan, setPlan] = useState(params.get("plan") || "ahu_monthly");
  const [busy, setBusy] = useState(false);
  const auto = useRef(false);

  useEffect(() => { sessionStorage.removeItem("ahu_prompt"); }, []);

  const join = async (lookup = plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      localStorage.removeItem("ahu_intent");
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  useEffect(() => {
    const p = params.get("plan");
    if (p && !auto.current && ["free", "lapsed"].includes(me.access) && me.profile?.area !== OUTSIDE_NZ) { auto.current = true; join(p); }
    // eslint-disable-next-line
  }, []);

  if (me.is_member) {
    return (
      <AppShell>
        <PageHead eyebrow="Membership" title="You're an AHU member" sub="Thank you. Manage your membership any time from Settings." />
        <Link to="/" className="btn-charcoal" data-testid="member-home-btn">Go to Home</Link>
      </AppShell>
    );
  }

  if (me.access === "registered") {
    return (
      <AppShell>
        <PageHead eyebrow="Membership" title="Your card is registered" sub={CARD_NOTE} />
        <Link to="/questions" className="btn-charcoal" data-testid="registered-start-plan-btn">Start your plan</Link>
      </AppShell>
    );
  }

  const overseas = me.profile?.area === OUTSIDE_NZ;
  const lapsed = me.access === "lapsed";
  if (me.access === "restricted") return <AppShell><CardFailed /></AppShell>;
  return (
    <AppShell>
      {lapsed ? <PageHead eyebrow="Membership" title="Rejoin AHU" sub="Your membership has ended. Make your membership payment to get back into AHU and your Action Plans." />
        : <PageHead eyebrow="Membership" title="A place to return to, between plans" sub="Your free plan is yours to keep. Membership is for when you're ready for more." />}
      {overseas ? <InterestPanel /> : (
      <div className="fade-in" data-testid="membership-page">
        <div className="grid gap-4 sm:grid-cols-2">
          {PRICING.filter((p) => p.id !== "free").map((p) => <PriceCard key={p.id} p={p} selected={!busy || plan === p.id} busy={busy} onSelect={(id) => { setPlan(id); join(id); }} />)}
        </div>
        {!lapsed && me.plans_created === 0 && <p className="mt-5 font-medium text-charcoal" data-testid="card-note">{CARD_NOTE}</p>}
        <p className="mt-4 text-sm text-charcoal" data-testid="gst-note">{NZ_GST_NOTE}</p>
        <p className="mt-5 text-sm text-ink-soft leading-relaxed" data-testid="refund-terms">{REFUND_TERMS} See our{" "}
          <Link to="/terms" className="underline underline-offset-4" data-testid="refund-terms-link">Terms of Service</Link>.</p>
        <p className="mt-2 text-sm text-ink-soft" data-testid="keep-note">{KEEP_NOTE}</p>
      </div>
      )}
      {!lapsed && me.plans_created > 0 && (
        <Link to="/plans" className="btn-quiet mt-8" data-testid="not-now-link">Not now, view my plan</Link>
      )}
      {!lapsed && me.plans_created === 0 && (
        <Link to="/" className="btn-quiet mt-8" data-testid="start-free-instead-link">Start with my free plan instead</Link>
      )}
      {lapsed && <Link to="/settings" className="btn-quiet mt-8" data-testid="lapsed-settings-link">Account settings and sign out</Link>}
    </AppShell>
  );
}

const CardFailed = () => {
  const [fix, busy] = useFixCard();
  return (
    <div className="ahu-card p-6 sm:p-8 fade-in" data-testid="membership-card-failed">
      <h1 className="text-2xl sm:text-3xl">Please update your card</h1>
      <p className="mt-3 text-ink leading-relaxed">{RESTRICTED_NOTE}</p>
      <button className="btn-charcoal mt-6 w-full sm:w-auto sm:min-w-[240px]" disabled={busy} onClick={fix} data-testid="membership-update-card-btn">{busy ? "Opening checkout" : "Update my card"}</button>
    </div>
  );
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const api = useApi();
  const nav = useNavigate();
  const { refresh } = useMe();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sid = params.get("session_id");
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (stop) return;
        if (data.payment_status === "paid") {
          await refresh();
          toast.success({ setup: "Your card is registered. Start your first Action Plan.", fix: "Thank you. Your card is updated." }[data.mode] || "Welcome to AHU membership");
          return nav("/", { replace: true });
        }
        if (data.status === "expired" || data.payment_status === "failed") { await refresh(); return setState("failed"); }
      } catch {}
      if (++tries > 15) return setState("slow");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [api, params, refresh, nav]);

  return (
    <AppShell nav={false}>
      <div className="ahu-card p-8 text-center fade-in" data-testid="payment-result">
        {state === "checking" && (<><div className="flex justify-center"><GoldDots /></div><p className="mt-6 text-ink-soft">Confirming your membership…</p></>)}
        {state === "paid" && (<>
          <h1 className="text-3xl" data-testid="payment-success-title">Welcome to AHU membership</h1>
          <p className="mt-3 text-ink-soft">Your membership is active. A confirmation is on its way to your inbox.</p>
          <button className="btn-charcoal mt-8" onClick={() => nav("/")} data-testid="payment-continue-btn">Continue</button>
        </>)}
        {(state === "failed" || state === "slow") && (<>
          <h1 className="text-2xl">{state === "failed" ? "That payment didn't go through" : "This is taking longer than usual"}</h1>
          <p className="mt-3 text-ink-soft">{state === "failed" ? "No money has been taken. You can try again any time." : "Check back in a minute. If your membership doesn't show, email support@teahusolutions.com."}</p>
          <button className="btn-charcoal mt-8" onClick={() => nav("/membership")} data-testid="payment-retry-btn">Back to membership</button>
        </>)}
      </div>
    </AppShell>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <AppShell nav={false}>
      <div className="ahu-card p-8 text-center fade-in" data-testid="payment-cancel">
        <h1 className="text-2xl">No worries</h1>
        <p className="mt-3 text-ink-soft">Your checkout was cancelled and nothing was charged.</p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <button className="btn-charcoal" onClick={() => nav("/membership")} data-testid="payment-cancel-retry-btn">Back to membership</button>
          <button className="btn-outline" onClick={() => nav("/")} data-testid="payment-cancel-home-btn">Go to Home</button>
        </div>
      </div>
    </AppShell>
  );
}
